/**
 * Admin Feature - Data Fetching Hooks
 *
 * All read-only hooks use TanStack React Query (useQuery) backed by
 * queryOptions factories in yoyQueryOptions.ts and adminQueryOptions.ts.
 *
 * Mutation/transaction hooks (useExportTransactions, useUserTransactions,
 * useCalendarTransactions, useRestoreAndExport, useAddMatter) use manual
 * useState/useEffect as they involve optimistic updates or expose setters.
 */

import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/shared/utils/request";
import {
  yoySummaryOptions,
  yoyTimeSeriesOptions,
  dailyViewOptions,
  goLiveDatesOptions,
  professionalsOptions,
} from "./yoyQueryOptions";
import {
  usageMetricsOptions,
  exportStatsOptions,
  feedbackDataOptions,
  dashboardMetricsOptions,
  availableWeeksOptions,
  timekeeperPerformanceOptions,
  utilizationOptions,
  experimentalOptions,
} from "./adminQueryOptions";
import { logger } from "@/shared/utils/logger";
import { toast } from "sonner";
import { showLegacyRouteStub } from "@/shared/utils/legacyRouteStubs";
import { Professional, UsageRecord, FeedbackItem } from "../utils/adminHelpers";
import {
  AttorneyExportStats,
  ExportStatsTotals,
  YoYSummary,
  YoYDataPoint,
  YoYGranularity,
  GoLiveRecord,
  AvailableWeek,
  PerformanceData,
  UtilizationData,
  DailyViewData,
  ExperimentalData,
} from "../types/dashboard";

// ─────────────────────────────────────────────
// useProfessionals Hook
// ─────────────────────────────────────────────

export interface UseProfessionalsOptions {
  /** Only return professionals who have tracked time in Hourglass */
  activeOnly?: boolean;
}

export interface UseProfessionalsResult {
  professionals: Professional[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch list of professionals (attorneys) for the firm.
 * Filters out test accounts.
 *
 * @param options.activeOnly - If true, only returns professionals with tracked time
 */
export function useProfessionals(
  options: UseProfessionalsOptions = {}
): UseProfessionalsResult {
  const { activeOnly = false } = options;
  const { makeApiRequest } = useApi();
  const query = useQuery(professionalsOptions(activeOnly, makeApiRequest));

  return {
    professionals: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useUsageMetrics Hook
// ─────────────────────────────────────────────

export interface UseUsageMetricsResult {
  usageData: UsageRecord[];
  globalExportTimes: Record<string, string | null>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch usage metrics for the specified timeframe.
 * Returns usage records and global export times.
 */
export function useUsageMetrics(timeframe: string): UseUsageMetricsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(usageMetricsOptions(timeframe, makeApiRequest));

  return {
    usageData: query.data?.usageData ?? [],
    globalExportTimes: query.data?.globalExportTimes ?? {},
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useExportStats Hook
// ─────────────────────────────────────────────

export interface UseExportStatsResult {
  attorneys: AttorneyExportStats[];
  totals: ExportStatsTotals | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch combined export statistics for all attorneys.
 * Returns attorney-level stats with exported/unexported hours and percentages.
 */
export function useExportStats(timeframe: string): UseExportStatsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(exportStatsOptions(timeframe, makeApiRequest));

  return {
    attorneys: query.data?.attorneys ?? [],
    totals: query.data?.totals ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useFeedbackData Hook
// ─────────────────────────────────────────────

export interface UseFeedbackDataResult {
  feedbackData: FeedbackItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch all feedback submissions for the support inbox.
 */
export function useFeedbackData(): UseFeedbackDataResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(feedbackDataOptions(makeApiRequest));

  return {
    feedbackData: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// Export Transaction Helpers
// ─────────────────────────────────────────────

export interface ExportPreviewResult {
  total: number;
  count: number;
  attorneys: number;
}

/**
 * Hook for handling CSV export functionality.
 */
export function useExportTransactions() {
  const { makeApiRequest } = useApi();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<ExportPreviewResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const clearPreview = useCallback(() => {
    setPreviewResult(null);
    setError(null);
  }, []);

  const previewHours = useCallback(
    async (
      startDate: string,
      endDate: string,
      profIds: string[]
    ): Promise<ExportPreviewResult | null> => {
      setPreviewLoading(true);
      setPreviewResult(null);
      setError(null);

      try {
        const response = await makeApiRequest({
          endpoint: "/admin/exportCSV",
          method: "POST",
          body: { startDate, endDate, profIds },
        });

        const data = response.data || [];

        if (data.length === 0) {
          setError("No exported entries found for the selected criteria.");
          return null;
        }

        // Calculate total hours from Duration field
        const totalHours = data.reduce(
          (sum: number, row: Record<string, unknown>) => {
            const duration = parseFloat(String(row.Duration || 0));
            return sum + (isNaN(duration) ? 0 : duration);
          },
          0
        );

        // Count unique attorneys
        const uniqueAttorneys = new Set(
          data.map(
            (row: Record<string, unknown>) =>
              row.Attorney || row.Professional || row.prof_id
          )
        ).size;

        const result = {
          total: Math.round(totalHours * 100) / 100,
          count: data.length,
          attorneys: uniqueAttorneys,
        };

        setPreviewResult(result);
        return result;
      } catch (e) {
        const errorMsg = "Failed to fetch preview data";
        logger.error(`${errorMsg}: ${e}`);
        toast.error(errorMsg, { duration: 5000 });
        return null;
      } finally {
        setPreviewLoading(false);
      }
    },
    [makeApiRequest]
  );

  const exportToCsv = useCallback(
    async (
      startDate: string,
      endDate: string,
      profIds: string[]
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const response = await makeApiRequest({
          endpoint: "/admin/exportCSV",
          method: "POST",
          body: { startDate, endDate, profIds },
        });

        const data = response.data || [];

        if (data.length === 0) {
          setError("No exported entries found for the selected criteria.");
          return false;
        }

        // Generate CSV
        const headers = [
          "Attorney",
          "MatterNo",
          "Duration",
          "Date",
          "WorkType",
          "Rate",
          "Description",
          "IsNoCharge",
          "IsNcds",
          "TaskCode",
          "ActivityCode",
        ];

        const csvRows = [
          headers.join(","),
          ...data.map((row: Record<string, unknown>) =>
            headers
              .map((header) => {
                const value = row[header];
                if (value === null || value === undefined) return "";
                const str = String(value);
                if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                  return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
              })
              .join(",")
          ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `exported_transactions_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success("CSV exported", {
          description: `${data.length} records downloaded`,
        });
        return true;
      } catch (e) {
        logger.error(`Failed to export data: ${e}`);
        toast.error("Failed to export CSV", {
          duration: 5000,
          description: e instanceof Error ? e.message : undefined,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [makeApiRequest]
  );

  return {
    loading,
    previewLoading,
    previewResult,
    error,
    clearPreview,
    previewHours,
    exportToCsv,
  };
}

// ─────────────────────────────────────────────
// useUserTransactions Hook
// ─────────────────────────────────────────────

/**
 * Transaction data from the admin user-transactions endpoint.
 * Includes original values for showing edit diffs.
 */
export interface AdminTransaction {
  id: number;
  prof_id: string;
  prof_name: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  units: number;
  original_units: number | null;
  description: string;
  original_description: string | null;
  matter_id: string | null;
  original_matter: string | null;
  matter_number: string | null;
  matter_description: string | null;
  task_code: string | null;
  activity_code: string | null;
  document_name: string | null;
  entry_id: string | null;
  exported: boolean;
  approved: boolean;
  needs_improvement: boolean;
  /** Rejection reason - null if not rejected, string value if rejected */
  deducted: string | null;
  /** Feedback for the attorney about this entry */
  attorney_feedback: string | null;
  /** Feedback for Hourglass to improve AI narrative generation */
  hourglass_feedback: string | null;
  app_name: string;
  modified_date: string | null;
  /** Source table - only present when includeDeleted=true is passed */
  source?: "transactions" | "deleted_transactions";
}

export interface UseUserTransactionsOptions {
  /** Include deleted transactions in results (default: false) */
  includeDeleted?: boolean;
}

export interface UseUserTransactionsResult {
  transactions: AdminTransaction[];
  setTransactions: React.Dispatch<React.SetStateAction<AdminTransaction[]>>;
  summary: {
    total_entries: number;
    total_hours: number;
    exported_count: number;
    unexported_count: number;
    deleted_count?: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch transactions for a specific attorney on a specific date.
 * Returns detailed transaction data including original values for diff display.
 *
 * Not converted to useAdminQuery because it exposes setTransactions for external mutation.
 *
 * @param date - The date to fetch transactions for (YYYY-MM-DD format)
 * @param profId - The professional ID to fetch transactions for
 * @param options.includeDeleted - If true, includes deleted_transactions via UNION
 */
export function useUserTransactions(
  date: string | null,
  profId: string | null,
  options: UseUserTransactionsOptions = {}
): UseUserTransactionsResult {
  const { includeDeleted = false } = options;
  const { makeApiRequest } = useApi();
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [summary, setSummary] = useState<UseUserTransactionsResult["summary"]>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    // Don't fetch if we don't have both required params
    if (!date || !profId) {
      setTransactions([]);
      setSummary(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const deletedParam = includeDeleted ? "&includeDeleted=true" : "";
      const response = await makeApiRequest({
        endpoint: `/admin/user-transactions?date=${date}&profIds=${profId}${deletedParam}`,
        method: "GET",
      });

      const data = response.data;
      if (data) {
        setTransactions(data.transactions || []);
        setSummary(data.summary || null);
      } else {
        setTransactions([]);
        setSummary(null);
      }
    } catch (e) {
      const errorMsg = "Failed to load transactions";
      logger.error(`${errorMsg}: ${e}`);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest, date, profId, includeDeleted]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, setTransactions, summary, loading, error, refetch: fetchTransactions };
}

// ─────────────────────────────────────────────
// useDashboardMetrics Hook
// ─────────────────────────────────────────────

export interface DashboardMetrics {
  monthName: string;
  unexportedHours: number;
  exportedHours: number;
  deletedHours: number;
  activeUsers: number;
}

export interface UseDashboardMetricsResult {
  metrics: DashboardMetrics | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch dashboard metrics for a date range.
 * If startDate/endDate are provided, filters to that range.
 * Otherwise defaults to current month (backend default).
 */
export function useDashboardMetrics(startDate?: string, endDate?: string): UseDashboardMetricsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(dashboardMetricsOptions(startDate, endDate, makeApiRequest));

  return {
    metrics: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useRestoreAndExport Hook
// ─────────────────────────────────────────────

export interface UseRestoreAndExportResult {
  restoreAndExport: (id: number) => Promise<boolean>;
}

/**
 * Hook for restoring a deleted entry and exporting it.
 * Two-step process:
 * 1. POST /admin/restore-entry — moves entry from deleted_transactions → transactions
 * 2. TODO: wire export to /v2/entries/export
 *
 * If restore succeeds but export fails, the entry remains restored as "tracked" (unexported).
 * Loading state is managed by the caller to cover the full operation including any post-action refetch.
 */
export function useRestoreAndExport(): UseRestoreAndExportResult {
  const { makeApiRequest } = useApi();

  const restoreAndExport = useCallback(
    async (id: number): Promise<boolean> => {
      const toastId = toast.loading("Restoring and exporting entry...");
      try {
        // Step 1: Restore the entry from deleted_transactions → transactions
        const restoreResp = await makeApiRequest({
          endpoint: "/admin/restore-entry",
          method: "POST",
          body: { id },
        });

        if (restoreResp.error) {
          throw new Error(String(restoreResp.error));
        }

        showLegacyRouteStub("/records/exportRecords", "/v2/entries/export");
        toast.success("Entry restored; export is not wired yet", { id: toastId });
        return true;
      } catch (e) {
        const errorMsg =
          e instanceof Error ? e.message : "Failed to restore entry";
        logger.error(`Restore and export failed: ${e}`);
        toast.error("Failed to restore entry", {
          id: toastId,
          duration: 5000,
          description: errorMsg,
        });
        return false;
      }
    },
    [makeApiRequest]
  );

  return { restoreAndExport };
}

// ─────────────────────────────────────────────
// useDailyView Hook
// ─────────────────────────────────────────────

export interface UseDailyViewResult {
  data: DailyViewData | null;
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch per-attorney daily billing hours for the daily overlay chart.
 * Returns after (since go-live) and before (1 year prior) daily arrays.
 * Requires a specific profId — not available for firm-wide view.
 * Query is disabled (no fetch) when profId is null.
 */
export function useDailyView(profId: string | null): UseDailyViewResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(dailyViewOptions(profId, makeApiRequest));

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useCalendarTransactions Hook
// ─────────────────────────────────────────────

export interface CalendarTransaction {
  id: number;
  prof_id: string;
  date: string;
  units: number;
  exported: boolean;
}

export interface UseCalendarTransactionsResult {
  transactions: CalendarTransaction[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch all transactions for a specific attorney in a given month.
 * Used by the Admin Calendar View to show daily hour summaries.
 *
 * Since the backend API only supports single-date queries, this hook makes
 * parallel requests for each day in the month and aggregates the results.
 *
 * @param monthDate - Any date within the target month (uses year/month only)
 * @param profId - The professional ID to fetch transactions for
 */
export function useCalendarTransactions(
  monthDate: Date | null,
  profId: string | null
): UseCalendarTransactionsResult {
  const { makeApiRequest } = useApi();
  const [transactions, setTransactions] = useState<CalendarTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    // Don't fetch if we don't have both required params
    if (!monthDate || !profId) {
      setTransactions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate all days in the month
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

      const formatDate = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      // Generate array of all dates in the month
      const dates: string[] = [];
      for (let day = 1; day <= lastDayOfMonth; day++) {
        dates.push(formatDate(new Date(year, month, day)));
      }

      // Make parallel requests for each day in the month
      // Using the existing single-date API: /admin/user-transactions?date=YYYY-MM-DD&profIds=X
      const requests = dates.map((date) =>
        makeApiRequest({
          endpoint: `/admin/user-transactions?date=${date}&profIds=${profId}`,
          method: "GET",
        }).catch(() => ({ data: { transactions: [] } })) // Handle individual failures gracefully
      );

      const responses = await Promise.all(requests);

      // Aggregate all transactions from all days
      const allTransactions: CalendarTransaction[] = [];
      for (const response of responses) {
        const data = response.data;
        if (data && data.transactions) {
          for (const tx of data.transactions as AdminTransaction[]) {
            allTransactions.push({
              id: tx.id,
              prof_id: tx.prof_id,
              date: tx.date,
              units: tx.units,
              exported: tx.exported,
            });
          }
        }
      }

      setTransactions(allTransactions);
    } catch (e) {
      const errorMsg = "Failed to load calendar transactions";
      logger.error(`${errorMsg}: ${e}`);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [makeApiRequest, monthDate, profId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, loading, error, refetch: fetchTransactions };
}

// ─────────────────────────────────────────────
// Add Matter Hook
// ─────────────────────────────────────────────

export function useAddMatter() {
  const { makeApiRequest } = useApi();
  const [loading, setLoading] = useState(false);

  const addMatter = useCallback(
    async (values: {
      matter_id: string;
      client: string;
      description: string;
      matter: string;
    }): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);

      try {
        const resp = await makeApiRequest({
          endpoint: "/matters/add",
          method: "POST",
          body: values,
        });

        if (resp.error) {
          const errorMsg = String(resp.error);
          if (errorMsg.includes("already exists") || resp.status === "ERROR") {
            return {
              success: false,
              error: "This Matter ID already exists. Please use a different ID.",
            };
          }
          return { success: false, error: errorMsg || "Failed to add matter" };
        }

        toast.success("Matter added");
        return { success: true };
      } catch (e) {
        logger.error(`Failed to add matter: ${e}`);
        toast.error("Failed to add matter", {
          duration: 5000,
          description: e instanceof Error ? e.message : undefined,
        });
        return { success: false, error: e instanceof Error ? e.message : "Failed to add matter" };
      } finally {
        setLoading(false);
      }
    },
    [makeApiRequest]
  );

  return { loading, addMatter };
}

// ─────────────────────────────────────────────
// useYoYSummary Hook
// ─────────────────────────────────────────────

export interface UseYoYSummaryResult {
  summary: YoYSummary | null;
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch go-live-anchored billing summary (total billed, hours, averages).
 * Before/After windows are computed server-side from each attorney's
 * Hourglass go-live date — no date params needed.
 */
export function useYoYSummary(
  profId: string | null
): UseYoYSummaryResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(yoySummaryOptions(profId, makeApiRequest));

  return {
    summary: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useYoYTimeSeries Hook
// ─────────────────────────────────────────────

export interface UseYoYTimeSeriesResult {
  data: YoYDataPoint[];
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch go-live-anchored time series breakdown (weekly or monthly).
 * Returns data points grouped by period, year, and professional.
 */
export function useYoYTimeSeries(
  granularity: YoYGranularity,
  profId: string | null
): UseYoYTimeSeriesResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(yoyTimeSeriesOptions(granularity, profId, makeApiRequest));

  return {
    data: query.data ?? [],
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useGoLiveDates Hook
// ─────────────────────────────────────────────

export interface UseGoLiveDatesResult {
  goLiveDates: GoLiveRecord[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch per-attorney Hourglass go-live dates.
 * Go-live = MIN(date) from transactions UNION deleted_transactions.
 */
export function useGoLiveDates(): UseGoLiveDatesResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(goLiveDatesOptions(makeApiRequest));

  return {
    goLiveDates: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useAvailableWeeks Hook
// ─────────────────────────────────────────────

export interface UseAvailableWeeksResult {
  weeks: AvailableWeek[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch distinct weeks that have billing data, newest first.
 * Used by the Timekeeper Performance tab's week navigator.
 */
export function useAvailableWeeks(): UseAvailableWeeksResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(availableWeeksOptions(makeApiRequest));

  return {
    weeks: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useTimekeeperPerformance Hook
// ─────────────────────────────────────────────

export interface UseTimekeeperPerformanceResult {
  performance: PerformanceData | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch rolling performance metrics for all timekeepers as of a given week end date.
 * Returns current week/month/YTD hours plus averages and prior-year comparisons.
 */
export function useTimekeeperPerformance(
  asOfDate: string | null
): UseTimekeeperPerformanceResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(timekeeperPerformanceOptions(asOfDate, makeApiRequest));

  return {
    performance: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useUtilization Hook
// ─────────────────────────────────────────────

export interface UseUtilizationResult {
  utilization: UtilizationData | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch Hourglass utilization vs total billing hours per attorney.
 * utilization_pct = hourglass_exported_hours / total_billing_hours * 100
 *
 * Reusable across tabs — any component can call this with its own date range.
 */
export function useUtilization(
  startDate: string,
  endDate: string
): UseUtilizationResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(utilizationOptions(startDate, endDate, makeApiRequest));

  return {
    utilization: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useExperimental Hook
// ─────────────────────────────────────────────

export interface UseExperimentalResult {
  data: ExperimentalData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch experimental weekly hours trend for all licensed professionals.
 * Returns pre-HG weekly average and weekly billed hours since go-live per attorney.
 */
export function useExperimental(): UseExperimentalResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(experimentalOptions(makeApiRequest));

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
